import {
	useUserAddLoginMutation,
	useUserChangeGroupsMutation,
	useUserSetEnabledMutation,
} from "@repo/server-api";
import { useNotification } from "@repo/shared/components";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import type { User } from "../types/UserTypes";

type CreateUserInput = {
	contactId: string;
	email: string;
	firstName: string;
	lastName: string;
	username: string;
	groupIds: string[];
	sendVerificationEmail: boolean;
};

type UseUserMutationsReturn = {
	createUser: (input: CreateUserInput) => Promise<boolean>;
	updateUser: (user: User) => Promise<boolean>;
	isLoading: boolean;
};

export function useUserMutations(): UseUserMutationsReturn {
	const { t } = useTranslation();
	const { showNotification } = useNotification();

	const [addLoginMutation, { loading: addLoading }] = useUserAddLoginMutation();
	const [changeGroupsMutation, { loading: groupsLoading }] = useUserChangeGroupsMutation();
	const [setEnabledMutation, { loading: enabledLoading }] = useUserSetEnabledMutation();

	const createUser = useCallback(
		async (input: CreateUserInput): Promise<boolean> => {
			try {
				const response = await addLoginMutation({
					variables: {
						contactId: input.contactId,
						email: input.email,
						firstName: input.firstName,
						lastName: input.lastName,
						username: input.username,
						groupIds: input.groupIds,
						sendVerificationEmail: input.sendVerificationEmail,
						clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
						redirectUri: window.location.origin,
					},
				});

				if (response.data?.userAddLogin.user) {
					showNotification(t("login.loginCreated"), "success");
					return true;
				}

				if (response.data?.userAddLogin.errors) {
					response.data.userAddLogin.errors.forEach((err) => {
						showNotification(err.message, "error");
					});
				}

				return false;
			} catch (error) {
				showNotification((error as Error).message, "error");
				return false;
			}
		},
		[addLoginMutation, showNotification, t],
	);

	const updateUser = useCallback(
		async (user: User): Promise<boolean> => {
			try {
				const [groupsResponse, enabledResponse] = await Promise.all([
					changeGroupsMutation({
						variables: {
							userId: user.id,
							groupIds: user.groups,
						},
					}),
					setEnabledMutation({
						variables: {
							userId: user.id,
							enabled: user.enabled,
						},
					}),
				]);

				let success = false;

				if (groupsResponse.data?.userChangeGroups.boolean) {
					showNotification(t("login.groupsChanged"), "success");
					success = true;
				} else if (groupsResponse.data?.userChangeGroups.errors) {
					groupsResponse.data.userChangeGroups.errors.forEach((err) => {
						showNotification(err.message, "error");
					});
				}

				if (enabledResponse.data?.userSetEnabled.boolean) {
					showNotification(t("login.enableChanged"), "success");
					success = true;
				} else if (enabledResponse.data?.userSetEnabled.errors) {
					enabledResponse.data.userSetEnabled.errors.forEach((err) => {
						showNotification(err.message, "error");
					});
				}

				return success;
			} catch (error) {
				showNotification((error as Error).message, "error");
				return false;
			}
		},
		[changeGroupsMutation, setEnabledMutation, showNotification, t],
	);

	return {
		createUser,
		updateUser,
		isLoading: addLoading || groupsLoading || enabledLoading,
	};
}
